import {logger} from '../../utils/logger.js';
import {UpdateAnnotationCommand} from '../../command/drawCommands.js';
import {validateAnchorPosition} from './drawBounds.js';
// external
import Konva from 'konva';

/**
 * @import {App} from '../../app/application.js';
 * @import {DrawLayer} from '../../gui/drawLayer.js';
 * @import {Annotation} from '../../image/annotation.js';
 */

/**
 * Draw shape editor.
 */
export class DrawShapeEditor {

  /**
   * Associated app.
   *
   * @type {App}
   */
  #app;

  /**
   * Event emitter.
   *
   * @type {Function}
   */
  #emitEvent;

  /**
   * @param {App} app The associated application.
   * @param {Function} emitEvent Event emitter.
   */
  constructor(app, emitEvent) {
    this.#app = app;
    this.#emitEvent = emitEvent;
  }

  /**
   * Current shape factory.
   *
   * @type {object|undefined}
   */
  #currentFactory;

  /**
   * Edited shape.
   *
   * @type {Konva.Shape|undefined}
   */
  #shape;

  /**
   * Associated draw layer. Used to bound anchor move.
   *
   * @type {DrawLayer}
   */
  #drawLayer;

  /**
   * The associated annotation.
   *
   * @type {Annotation}
   */
  #annotation;

  /**
   * Active flag.
   *
   * @type {boolean}
   */
  #isActive = false;

  /**
   * @callback eventFn
   * @param {object} event The event.
   */

  /**
   * Set the shape to edit.
   *
   * @param {Konva.Shape} inshape The shape to edit.
   * @param {DrawLayer} drawLayer The associated draw layer.
   * @param {Annotation} annotation The associated annotation.
   */
  setShape(inshape, drawLayer, annotation) {
    this.#shape = inshape;
    this.#drawLayer = drawLayer;
    this.#annotation = annotation;

    if (this.#shape !== undefined) {
      // remove old anchors
      this.#removeAnchors();

      this.#currentFactory = annotation.getFactory();
      if (typeof this.#currentFactory === 'undefined') {
        throw new Error('Could not find a factory to update shape.');
      }

      // add new anchors
      this.#addAnchors();
    }
  }

  /**
   * Get the edited shape.
   *
   * @returns {Konva.Shape|undefined} The edited shape.
   */
  getShape() {
    return this.#shape;
  }

  /**
   * Get the edited annotation.
   *
   * @returns {Annotation} The annotation.
   */
  getAnnotation() {
    return this.#annotation;
  }

  /**
   * Get the active flag.
   *
   * @returns {boolean} The active flag.
   */
  isActive() {
    return this.#isActive;
  }

  /**
   * Enable the editor. Redraws the layer.
   */
  enable() {
    this.#isActive = true;
    if (this.#shape !== undefined) {
      this.#setAnchorsVisible(true);
      if (this.#shape.getLayer()) {
        this.#shape.getLayer().draw();
      }
    }
  }

  /**
   * Disable the editor. Redraws the layer.
   */
  disable() {
    this.#isActive = false;
    if (this.#shape !== undefined) {
      this.#setAnchorsVisible(false);
      if (this.#shape.getLayer()) {
        this.#shape.getLayer().draw();
      }
    }
  }

  /**
   * Reset the editor.
   */
  reset() {
    this.#shape = undefined;
    this.#drawLayer = undefined;
    this.#annotation = undefined;
  }

  /**
   * Reset the anchors.
   */
  resetAnchors() {
    // remove previous controls
    this.#removeAnchors();
    // add anchors
    this.#addAnchors();
    // set them visible
    this.#setAnchorsVisible(true);
  }

  /**
   * Apply a function on all anchors.
   *
   * @param {object} func A f(shape) function.
   */
  #applyFuncToAnchors(func) {
    if (this.#shape !== undefined && this.#shape.getParent()) {
      const anchors = this.#shape.getParent().find('.anchor');
      anchors.forEach(func);
    }
  }

  /**
   * Set anchors visibility.
   *
   * @param {boolean} flag The visible flag.
   */
  #setAnchorsVisible(flag) {
    this.#applyFuncToAnchors(function (anchor) {
      anchor.visible(flag);
    });
  }

  /**
   * Set anchors active.
   *
   * @param {boolean} flag The active (on/off) flag.
   */
  setAnchorsActive(flag) {
    let func;
    if (flag) {
      func = (anchor) => {
        this.#setAnchorOn(anchor);
      };
    } else {
      func = (anchor) => {
        this.#setAnchorOff(anchor);
      };
    }
    this.#applyFuncToAnchors(func);
  }

  /**
   * Remove anchors.
   */
  #removeAnchors() {
    this.#applyFuncToAnchors(function (anchor) {
      anchor.remove();
    });
  }

  /**
   * Add the shape anchors.
   */
  #addAnchors() {
    // exit if no shape or no layer
    if (this.#shape === undefined || !this.#shape.getLayer()) {
      return;
    }
    if (this.#currentFactory === undefined) {
      return;
    }
    // get shape group
    const group = this.#shape.getParent();

    // activate and add anchors to group
    const anchors =
      this.#currentFactory.getAnchors(this.#annotation, this.#app.getStyle());
    for (let i = 0; i < anchors.length; ++i) {
      // set anchor on
      this.#setAnchorOn(anchors[i]);
      // add the anchor to the group
      group.add(anchors[i]);
    }
  }

  /**
   * Set the anchor on listeners.
   *
   * @param {Konva.Ellipse} anchor The anchor to set on.
   */
  #setAnchorOn(anchor) {
    let originalProps;

    // drag start listener
    anchor.on('dragstart.edit', (event) => {
      // prevent bubbling upwards
      event.cancelBubble = true;
      // store original properties
      originalProps = {
        mathShape: this.#annotation.mathShape,
        referencePoints: this.#annotation.referencePoints
      };
    });
    // drag move listener
    anchor.on('dragmove.edit', (event) => {
      const evAnchor = event.target;
      if (!(evAnchor instanceof Konva.Shape)) {
        return;
      }
      if (this.#currentFactory === undefined) {
        return;
      }
      // validate the anchor position
      validateAnchorPosition(this.#drawLayer.getBaseSize(), evAnchor);
      if (typeof this.#currentFactory.constrainAnchorMove !== 'undefined') {
        this.#currentFactory.constrainAnchorMove(
          evAnchor, this.#annotation);
      }

      // udpate annotation
      this.#currentFactory.updateAnnotationOnAnchorMove(
        this.#annotation, evAnchor);
      // udpate shape
      this.#currentFactory.updateShapeGroupOnAnchorMove(
        this.#annotation, evAnchor, this.#app.getStyle());

      // redraw
      if (evAnchor.getLayer()) {
        evAnchor.getLayer().draw();
      } else {
        logger.warn('No layer to draw the anchor!');
      }
      // prevent bubbling upwards
      event.cancelBubble = true;
    });
    // drag end listener
    anchor.on('dragend.edit', (event) => {
      // tell factory
      const evAnchor = event.target;
      if (!(evAnchor instanceof Konva.Shape)) {
        return;
      }
      this.#currentFactory?.onAnchorMoveEnd(
        evAnchor, this.#annotation);

      // update annotation command
      const newProps = {
        mathShape: this.#annotation.mathShape,
        referencePoints: this.#annotation.referencePoints
      };
      const command = new UpdateAnnotationCommand(
        this.#annotation,
        originalProps,
        newProps,
        this.#drawLayer.getDrawController()
      );
      // add command to undo stack
      const undoCtrl = this.#app.getUndoController();
      undoCtrl.addToUndoStack(command);
      // fire event manually since command is not executed
      this.#emitEvent({
        type: 'annotationupdate',
        data: this.#annotation,
        dataid: this.#drawLayer.getDataId(),
        keys: Object.keys(newProps)
      });
      // update original properties
      originalProps = {
        mathShape: newProps.mathShape,
        referencePoints: newProps.referencePoints
      };

      // prevent bubbling upwards
      event.cancelBubble = true;
    });
    // mouse down listener
    anchor.on('mousedown touchstart', (event) => {
      const evAnchor = event.target;
      evAnchor.moveToTop();
    });
    // mouse over styling
    anchor.on('mouseover.edit', (event) => {
      const evAnchor = event.target;
      if (!(evAnchor instanceof Konva.Shape)) {
        return;
      }
      // style is handled by the group
      evAnchor.stroke('#ddd');
      if (evAnchor.getLayer()) {
        evAnchor.getLayer().draw();
      } else {
        logger.warn('No layer to draw the anchor!');
      }
    });
    // mouse out styling
    anchor.on('mouseout.edit', (event) => {
      const evAnchor = event.target;
      if (!(evAnchor instanceof Konva.Shape)) {
        return;
      }
      // style is handled by the group
      evAnchor.stroke('#999');
      if (evAnchor.getLayer()) {
        evAnchor.getLayer().draw();
      } else {
        logger.warn('No layer to draw the anchor!');
      }
    });
  }

  /**
   * Set the anchor off listeners.
   *
   * @param {Konva.Ellipse} anchor The anchor to set off.
   */
  #setAnchorOff(anchor) {
    anchor.off('dragstart.edit');
    anchor.off('dragmove.edit');
    anchor.off('dragend.edit');
    anchor.off('mousedown touchstart');
    anchor.off('mouseover.edit');
    anchor.off('mouseout.edit');
  }

} // class Editor
